



import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from './Pagination';

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    phoneNumber: '',
    industry: '',
    terms: false,
    avatar: null,
    coverImage: null
  });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 5; // Matches API limit

  // Fetch users with pagination
  const fetchUsers = (page = 1) => {
    setLoading(true);
    
    // Get the access token from localStorage (set during login)
    const accessToken = localStorage.getItem('accessToken');
    
    if (!accessToken) {
      setError("You are not authenticated. Please login first.");
      setLoading(false);
      return;
    }
    
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `https://devserver.spatialgrid.ai/api/v1/users/users-list?limit=${limit}&page=${page}`,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    };

    axios
      .request(config)
      .then((response) => {
        setUsers(response.data.users || []);
        setTotalPages(Math.ceil(response.data.total / limit) || 1);
        setLoading(false);
      })
      .catch((err) => {
        console.error('API error:', err);
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          setError("Authentication error. Please login again.");
        } else {
          setError(err.message || "Failed to fetch users");
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const body = new FormData();
    body.append('name', formData.name);
    body.append('email', formData.email);
    body.append('password', formData.password);
    body.append('password_confirmation', formData.password_confirmation);
    body.append('phoneNumber', formData.phoneNumber);
    body.append('terms_conditions', true);
    if (formData.industry) body.append('industry', formData.industry);
    
    // Add avatar and coverImage only if they exist
    if (formData.avatar) {
      body.append('avatar', formData.avatar);
    }
    if (formData.coverImage) {
      body.append('coverImage', formData.coverImage);
    }
    
    // Get the access token from localStorage (set during login)
    const accessToken = localStorage.getItem('accessToken');
    
    if (!accessToken) {
      setError("You are not authenticated. Please login first.");
      return;
    }
    
    // Note: Don't set Content-Type header when using FormData
    // The browser will automatically set the correct Content-Type with boundary
    fetch('https://devserver.spatialgrid.ai/api/v1/users/register', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: body
    })
      .then((res) => {
        console.log('Response status:', res.status);
        
        if (!res.ok) {
          return res.json().then(errorData => {
            console.error('API Error Response:', errorData);
            throw new Error(errorData.message || 'Failed to add user');
          });
        }
        return res.json();
      })
      .then((data) => {
        console.log('User successfully added:', data);
        setShowForm(false);
        setFormData({
          name: '',
          email: '',
          password: '',
          password_confirmation: '',
          phoneNumber: '',
          industry: '',
          terms: false,
          avatar: null,
          coverImage: null
        });
        fetchUsers(currentPage);
      })
      .catch((err) => {
        console.error('Add user error:', err);
        setError(err.message);
        alert(`Error: ${err.message}`);
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle status change
  const handleStatusChange = (userId, newStatus) => {
    // Get the access token from localStorage
    const accessToken = localStorage.getItem('accessToken');
    
    if (!accessToken) {
      setError("You are not authenticated. Please login first.");
      return;
    }
    
    // Make an actual API call to update user status
    axios.patch(`https://devserver.spatialgrid.ai/api/v1/users/update-status/${userId}`, 
      { status: newStatus },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        // Update local state to reflect the change
        setUsers(users.map(user => 
          user._id === userId ? {...user, status: newStatus} : user
        ));
      })
      .catch(err => {
        console.error('Failed to update status:', err);
        alert(`Error updating status: ${err.message || "Unknown error"}`);
      });
  };

  if (loading) return <p className="p-4">Loading users...</p>;
  if (error) return <p className="p-4 text-red-500">Error: {error}</p>;

  return (
    <div className="bg-[#1E1F22] m-5 rounded-lg w-full p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl m-0 text-gray-300">Users</h2>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Add User'}
        </button>
      </div>

      {/* Form for adding a new user */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 p-4 border border-gray-300 rounded">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded bg-[#2A2A2B] text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded bg-[#2A2A2B] text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded bg-[#2A2A2B] text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password Confirmation</label>
              <input
                type="password"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded bg-[#2A2A2B] text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone Number</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded bg-[#2A2A2B] text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Industry</label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded bg-[#2A2A2B] text-white"
              >
                <option value="">Select industry…</option>
                <option value="Manufacturing & Industrial">Manufacturing &amp; Industrial</option>
                <option value="Healthcare and Pharma">Healthcare and Pharma</option>
                <option value="Retail & E-Commerce">Retail &amp; E-Commerce</option>
                <option value="Education & Training">Education &amp; Training</option>
                <option value="Automotive & Transportation">Automotive &amp; Transportation</option>
                <option value="Architecture & Real Estate">Architecture &amp; Real Estate</option>
                <option value="Defense & Aerospace">Defense &amp; Aerospace</option>
                <option value="Energy and Mining">Energy and Mining</option>
                <option value="Tourism & Hospitality">Tourism &amp; Hospitality</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Avatar</label>
              <input
                type="file"
                name="avatar"
                onChange={handleFileChange}
                accept="image/*"
                className="w-full p-2 border border-gray-300 rounded bg-[#2A2A2B] text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cover Image</label>
              <input
                type="file"
                name="coverImage"
                onChange={handleFileChange}
                accept="image/*"
                className="w-full p-2 border border-gray-300 rounded bg-[#2A2A2B] text-white"
              />
            </div>
          </div>
          <button
            type="submit"
            className="mt-4 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            Submit
          </button>
        </form>
      )}

      <table className="w-full table-auto border-collapse border border-gray-300">
        <thead>
          <tr className="bg-[#2A2A2B] text-white">
            <th className="border border-gray-300 p-2">ID</th>
            <th className="border border-gray-300 p-2">Name</th>
            <th className="border border-gray-300 p-2">Email</th>
            <th className="border border-gray-300 p-2">Mobile Number</th>
            <th className="border border-gray-300 p-2">Industry</th>
            <th className="border border-gray-300 p-2">Role</th>
            <th className="border border-gray-300 p-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <tr key={user._id}>
              <td className="border border-gray-300 p-2">{(currentPage - 1) * limit + index + 1}</td>
              <td className="border border-gray-300 p-2">{user.name}</td>
              <td className="border border-gray-300 p-2">{user.email}</td>
              <td className="border border-gray-300 p-2">{user.phoneNumber}</td>
              <td className="border border-gray-300 p-2">{user.industry || '—'}</td>
              <td className="border border-gray-300 p-2">{user.role}</td>
              <td className="border border-gray-300 p-2">
                <select 
                  value={user.status} 
                  onChange={(e) => handleStatusChange(user._id, e.target.value)}
                  className="bg-[#2A2A2B] text-white border border-gray-300 rounded p-1 w-full"
                >
                  <option value="active">active</option>
                  <option value="deactive">unactive</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Component */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default Users;